import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import CloverPointConfig from 'types/models/CloverPointConfig';
import Venue from 'types/models/Venue';
import UserRoleType from 'types/enums/UserRoleType';
import EditCloverPointConfigContext from 'contexts/EditCloverPointConfigContext';
import AppContext from 'contexts/AppContext';
import { getCloverPointConfigs } from 'services/cloverServices';
import { getVenue } from 'services/venuesServices';
import useSetupInitialState from 'hooks/useSetupInitialState';

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { authData } = React.useContext(AppContext);
  const { setSelectedCloverPointConfig } = React.useContext(EditCloverPointConfigContext);
  const [paginationCloverConfig, setPaginationCloverConfigs] = useState<PaginationData<CloverPointConfig> | null>(null);
  const [venue, setVenue] = React.useState<Venue | null>(null);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user.role === UserRoleType.VenueEditor;

  const goToPage = (url: string): void => {
    history.push(url);
  };

  React.useEffect(() => {
    loadData();
  }, []);

  React.useEffect(() => {
    if (!entity) return;
    const getCompleteVenue = async () => {
      const completeVenue = await getVenue(entity.id);
      if (!completeVenue) return;
      setVenue(completeVenue);
    };
    getCompleteVenue();
  }, [entity]);

  const loadData = React.useCallback(async (page?: number) => {
    const paginationData = await getCloverPointConfigs(page, isVenueEditor);
    if (paginationData) setPaginationCloverConfigs(paginationData);
  }, []);

  const renderEditButton = (cloverPointConfig: CloverPointConfig): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        disabled={isVenueEditor && venue?.paymentConfig?.cloverPointConfigId !== cloverPointConfig.id}
        onClick={() => {
          setSelectedCloverPointConfig(cloverPointConfig);
          history.push('clover-point/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: CloverPointConfig[]): JSX.Element => {
    return (
      <>
        {paginationData.map((cloverConfig: CloverPointConfig) => (
          <TableRow key={cloverConfig.description}>
            <TableCell component="th" scope="row">
              {cloverConfig.description}
            </TableCell>
            <TableCell component="th" scope="row">
              {cloverConfig.friendlyId}
            </TableCell>
            <TableCell>{renderEditButton(cloverConfig)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="primary" onClick={() => goToPage('clover-point/create')}>
          {t('listCloverPointConfigs-page-create')}
        </Button>
      </div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="secondary" onClick={() => history.go(-1)}>
          {t('go-back')}
        </Button>
      </div>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listCloverPointConfigs-page-description')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-friendly-id')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationCloverConfig?.data && paginationCloverConfig?.data.length > 0 ? (
              renderContent(paginationCloverConfig.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listCloverPointConfigs-page-noConfigs')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationCloverConfig && (
        <TablePagination
          component="div"
          count={paginationCloverConfig.total}
          page={paginationCloverConfig.currentPage - 1}
          onPageChange={(_, page: number) => {
            loadData(page + 1);
          }}
          rowsPerPage={paginationCloverConfig.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </div>
  );
};
